
import User from "@/domain/model/user/User.js";
import RcDomainObject from "../../common/RcDomainObject.js";
import RegistrationMap from "./RegistrationMap.js";
import StringUtils from "@/utils/StringUtils.js";
import Company from "../company/Company.js";
import Employee from "../employee/Employee.js";
import Credentials from "../user/Credentials.js";
import RcDate from "../../session/time/RcDate.js";

export default class Registration extends RcDomainObject {

  static MODEL_NAME = 'Registration';

  static Fields = {
    COMPANY_NAME: "companyName",
    FIRST_REMINDER_DATE: "firstReminderEmailDate",
    FINAL_REMINDER_DATE: "finalReminderEmailDate",
  }

  static Values = {
    States: {
      // Set by the server when the admin creates the company from a registration.
      COMPANY_CREATED: "companycreated",
    },
  }

  constructor(domain, data) {
    super(domain, data, Registration.MODEL_NAME);
  }

  fields() {
    return Registration.Fields;
  }

  find() {
    return this.domain.registrations().findById(this.id());
  }

  update() {
    var update = this.find();
    this.data = update.data;
    return this;
  }

  stats() {
    return this.domain.registrations().findStatsById(this.id());
  }

  pointer() {
    var registration = new Registration(this.domain);
    registration.withId(this.id());
    return registration;
  }

  map() {
    var map = new RegistrationMap(this.domain);
    map.add(this);
    return map;
  }

  modifiedBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.MODIFIED_BY));
  }

  createdBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.CREATED_BY));
  }

  stateDisplay() {
    let state = this.state();
    if (StringUtils.startsWith(state, "active")) {
      return "Active"
    }
    if (StringUtils.startsWith(state, "inactive")) {
      return "Inactive"
    }
    if (StringUtils.startsWith(state, "deleted")) {
      return "Deleted"
    }
    if (StringUtils.startsWith(state, Registration.Values.States.COMPANY_CREATED)) {
      return "Company created"
    }
    return null;
  }

  /*
   * The sign-up form saves the role's free text as companyRoleOtherDetails, while
   * the server model declares companyRoleDetails, so read whichever the record has.
   */
  roleDetails() {
    const value = this.get('companyRoleOtherDetails') || this.get('companyRoleDetails') || '';
    return String(value).trim();
  }

  // The admin registrations table's wording for each state; stateDisplay() above is older.
  stateLabel() {
    const state = (this.state() || '').toLowerCase();
    switch (state) {
      case 'active': return 'Active';
      case 'inactive': return 'Waiting on user verification';
      case Registration.Values.States.COMPANY_CREATED: return 'Company created';
      case 'deleted': return 'Removed';
      default: return this.state();
    }
  }

  stateColour() {
    const state = (this.state() || '').toLowerCase();
    switch (state) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case Registration.Values.States.COMPANY_CREATED: return 'info';
      case 'deleted': return 'black';
      default: return 'grey';
    }
  }

  company() {
    return new Company(this.domain, this.get(Company.MODEL_NAME));
  }

  user() {
    return new User(this.domain, this.get(User.MODEL_NAME));
  }
  employee() {
    return new Employee(this.domain, this.get(Employee.MODEL_NAME));
  }
  credentials() {
    return new Credentials(this.domain, this.get(Credentials.MODEL_NAME));
  }

  hasFirstReminderDate() {
    return this.has(Registration.Fields.FIRST_REMINDER_DATE) && this.getInt(Registration.Fields.FIRST_REMINDER_DATE) > 0;
  }

  firstReminderDate() {
    var date = this.getInt(Registration.Fields.FIRST_REMINDER_DATE);
    return new RcDate(new Date(date), this.timeZone());
  }

  hasFinalReminderDate() {
    return this.has(Registration.Fields.FINAL_REMINDER_DATE) && this.getInt(Registration.Fields.FINAL_REMINDER_DATE) > 0;
  }

  finalReminderDate() {
    var date = this.getInt(Registration.Fields.FINAL_REMINDER_DATE);
    return new RcDate(new Date(date), this.timeZone());
  }
}